/**
 * Shared Atlas item-icon helpers.
 *
 * Same CDN used by `ItemIcon`, economy listings, spawn limits, etc.:
 *   https://atlas.playcdu.co/img/{cluster}/{namespace}/{id}/0
 *   https://atlas.playcdu.co/search/first/{namespace}/{id}   (fallback)
 *
 * The canvas quest viewer draws HTMLImageElements from this cache so a
 * chapter with hundreds of icons does not mount hundreds of React nodes.
 */

export const ATLAS_BASE_URL = 'https://atlas.playcdu.co';

/** Network-wide fallback used by spawn limits / item bans when no cluster is known. */
export const ATLAS_DEFAULT_CLUSTER = '_network';

export interface AtlasIconRecord {
  src: string;
  displayName: string | null;
  image: HTMLImageElement | null;
}

const recordCache = new Map<string, AtlasIconRecord>();
const inflight = new Map<string, Promise<AtlasIconRecord | null>>();
const imageCache = new Map<string, HTMLImageElement>();

export function atlasCacheKey(clusterId: string, itemId: string): string {
  return `${clusterId}\0${itemId}`;
}

export function parseNamespacedId(itemId: string): { namespace: string; id: string } | null {
  if (!itemId || !itemId.includes(':')) return null;
  const [namespace, ...rest] = itemId.split(':');
  const id = rest.join(':');
  if (!namespace || !id) return null;
  return { namespace, id };
}

export function atlasPrimaryUrl(clusterId: string, namespace: string, id: string): string {
  return `${ATLAS_BASE_URL}/img/${encodeURIComponent(clusterId)}/${encodeURIComponent(namespace)}/${encodeURIComponent(id)}/0`;
}

export function atlasFallbackUrl(namespace: string, id: string): string {
  return `${ATLAS_BASE_URL}/search/first/${encodeURIComponent(namespace)}/${encodeURIComponent(id)}`;
}

function decodeDisplayName(headers: Headers): string | null {
  const raw = headers.get('X-Display-Name');
  if (!raw) return null;
  try {
    return decodeURIComponent(raw).replace(/\+/g, ' ');
  } catch {
    return raw.replace(/\+/g, ' ');
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  const cached = imageCache.get(src);
  if (cached && cached.complete && cached.naturalWidth > 0) return Promise.resolve(cached);
  return new Promise((resolve, reject) => {
    const img = cached ?? new Image();
    img.decoding = 'async';
    img.onload = () => {
      imageCache.set(src, img);
      resolve(img);
    };
    img.onerror = () => reject(new Error(`Failed to load atlas image ${src}`));
    if (img.src !== src) img.src = src;
    else if (img.complete && img.naturalWidth > 0) {
      imageCache.set(src, img);
      resolve(img);
    }
  });
}

async function fetchOk(url: string): Promise<Response | null> {
  try {
    const response = await fetch(url);
    return response.ok ? response : null;
  } catch {
    return null;
  }
}

export function getCachedAtlasIcon(clusterId: string, itemId: string): AtlasIconRecord | undefined {
  return recordCache.get(atlasCacheKey(clusterId, itemId));
}

export function getCachedAtlasImage(clusterId: string, itemId: string): HTMLImageElement | null {
  return recordCache.get(atlasCacheKey(clusterId, itemId))?.image ?? null;
}

/**
 * Resolve an item icon the same way `ItemIcon` does (primary cluster URL,
 * then `/search/first`). Caches both metadata and the decoded image.
 */
export function resolveAtlasIcon(clusterId: string, itemId: string): Promise<AtlasIconRecord | null> {
  const key = atlasCacheKey(clusterId, itemId);
  const existing = recordCache.get(key);
  if (existing) return Promise.resolve(existing);
  const pending = inflight.get(key);
  if (pending) return pending;

  const parsed = parseNamespacedId(itemId);
  if (!parsed) return Promise.resolve(null);

  const work = (async () => {
    const primary = atlasPrimaryUrl(clusterId, parsed.namespace, parsed.id);
    const fallback = atlasFallbackUrl(parsed.namespace, parsed.id);
    const response = (await fetchOk(primary)) ?? (await fetchOk(fallback));
    if (!response) return null;
    const src = response.url || primary;
    let image: HTMLImageElement | null = null;
    try {
      image = await loadImage(src);
    } catch {
      image = null;
    }
    const record: AtlasIconRecord = {
      src,
      displayName: decodeDisplayName(response.headers),
      image,
    };
    recordCache.set(key, record);
    return record;
  })().finally(() => {
    inflight.delete(key);
  });

  inflight.set(key, work);
  return work;
}

/** Prefetch a batch without blocking the caller. Dedupes via the shared cache. */
export function prefetchAtlasIcons(
  clusterId: string,
  itemIds: Iterable<string>,
  concurrency = 12,
  onEach?: (itemId: string, record: AtlasIconRecord | null) => void,
): Promise<void> {
  const unique: string[] = [];
  const seen = new Set<string>();
  for (const id of itemIds) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    unique.push(id);
  }
  if (unique.length === 0) return Promise.resolve();

  let cursor = 0;
  const workers = Array.from({ length: Math.min(concurrency, unique.length) }, async () => {
    while (cursor < unique.length) {
      const id = unique[cursor++];
      const record = await resolveAtlasIcon(clusterId, id);
      onEach?.(id, record);
    }
  });
  return Promise.all(workers).then(() => undefined);
}

/** Test-only: drop cached records so isolation stays honest. */
export function _resetAtlasIconCacheForTests(): void {
  recordCache.clear();
  inflight.clear();
  imageCache.clear();
}
